/**
 * Field_Ops client-side de la Capa Operacional AI-First (tarea 9.1).
 *
 * Implementa la interfaz {@link FieldOps} de `../api/contracts` en su
 * variante **demo** (client-side, R17.1/R17.5), cubriendo el Núcleo 8
 * (Field Operations — terreno realtime y offline-first):
 *
 *   - {@link FieldOpsLocal.track}: ubicación en tiempo real de una
 *     Cuadrilla, **solo a usuarios autorizados** según la matriz RBAC, con
 *     una cadencia de actualización ≤ 10 s ({@link TRACK_INTERVAL_MS})
 *     (R7.6, R8.1).
 *   - {@link FieldOpsLocal.route}: optimización **determinista** de la
 *     secuencia de visitas por *nearest-neighbor* con distancias geodésicas
 *     de Turf.js, y ETA por orden; dos cálculos sucesivos sobre las mismas
 *     órdenes/ubicaciones/parámetros producen la misma ruta (R8.2, R8.3,
 *     R8.4, Property 41).
 *   - **Trabajo offline**: las Ordenes_Inspeccion asignadas se conservan en
 *     IndexedDB ({@link OfflineOrderStore}, con *fallback* en memoria para
 *     Node/Vitest) para consultarlas y capturar evidencia sin conectividad
 *     (R8.5).
 *   - {@link FieldOpsLocal.reconcile}: reconciliación offline-first **sin
 *     pérdida**, con resolución de conflictos **determinista y trazable**
 *     (criterio registrado) y registro de toda evidencia capturada como
 *     Evento_Operacional con autor, timestamp y referencia a la
 *     Orden_Inspeccion (R8.6, R8.9, Property 42).
 *   - {@link FieldOpsLocal.associateEvidence}: asociación de evidencia a una
 *     Orden_Inspeccion vía NFC/QR; si el identificador no corresponde al
 *     Medidor/punto de servicio de la orden activa, **rechaza**, **preserva
 *     la evidencia** y devuelve error (R8.7, R8.8, R8.9).
 *
 * Las funciones puras {@link optimizarRuta}, {@link reconciliar} y
 * {@link nfcCoincide} se exportan para que las tareas de testing PBT
 * (Properties 41, 42) las ejerciten sin IndexedDB ni instanciar la clase.
 */

import type {
  AsociacionEvidencia,
  Coordenada,
  EntidadRef,
  EstadoCuadrilla,
  EventStore,
  EventoOperacional,
  FieldOps,
  Id,
  IsoTimestamp,
  Posicion,
  Result,
  ResultadoReconciliacion,
  Rol,
  RutaOptimizada,
} from "../api/contracts";

import { distance, point } from "@turf/turf";

// ---------------------------------------------------------------------------
// Constantes y tipos auxiliares
// ---------------------------------------------------------------------------

/** Cadencia máxima de actualización del tracking de una Cuadrilla (R7.6, R8.1). */
export const TRACK_INTERVAL_MS = 10_000;

/** Velocidad media por defecto (km/h) para estimar ETA de la ruta. */
export const VELOCIDAD_DEFECTO_KMH = 30;

/**
 * Roles autorizados por defecto a recibir la ubicación en tiempo real de
 * las Cuadrillas (R7.6, R8.1). Coordinan o supervisan terreno.
 */
export const ROLES_TRACKING_AUTORIZADOS: readonly Rol[] = [
  "Despachador",
  "Supervisor",
  "Operador",
] as const;

/** Una Orden_Inspeccion con ubicación e identificador de punto de servicio. */
export interface OrdenUbicada {
  /** Referencia a la Orden_Inspeccion de la fundación (R17). */
  orden_ref: EntidadRef;
  /** Ubicación geográfica del sitio de la orden. */
  coord: Coordenada;
  /**
   * Identificador NFC/QR esperado del Medidor o punto de servicio de la
   * orden (para la validación de {@link FieldOpsLocal.associateEvidence}).
   */
  identificador?: string;
}

/** Parámetros de cálculo de la ruta y el ETA (R8.2, R8.4). */
export interface ParametrosRuta {
  /** Velocidad media (km/h) usada para estimar el ETA. */
  velocidad_kmh: number;
  /** Instante de inicio del recorrido (base para acumular el ETA). */
  inicio: IsoTimestamp;
}

const PARAMETROS_RUTA_DEFECTO: ParametrosRuta = {
  velocidad_kmh: VELOCIDAD_DEFECTO_KMH,
  inicio: "2025-01-01T00:00:00.000Z",
};

// ---------------------------------------------------------------------------
// optimizarRuta — nearest-neighbor determinista con Turf.js (Property 41)
// ---------------------------------------------------------------------------

/** Distancia geodésica (km) entre dos coordenadas con Turf.js. */
function distanciaKm(a: Coordenada, b: Coordenada): number {
  return distance(point([a.lon, a.lat]), point([b.lon, b.lat]), { units: "kilometers" });
}

/** Clave estable de una orden para desempates deterministas. */
function ordenKey(ref: EntidadRef): string {
  return `${ref.tipo}:${ref.id}`;
}

/**
 * Optimiza la secuencia de visitas de un conjunto de Ordenes_Inspeccion por
 * **nearest-neighbor** partiendo de `origen` (la ubicación de la Cuadrilla),
 * y calcula el ETA acumulado por orden con la velocidad dada (R8.4).
 *
 * Es una **función pura y determinista**: dado el mismo origen, el mismo
 * conjunto de órdenes/ubicaciones y los mismos parámetros, produce siempre
 * la misma secuencia (Property 41). Los empates de distancia se resuelven
 * de forma determinista por la clave de la orden ascendente, de modo que el
 * resultado no depende del orden de entrada de las órdenes.
 */
export function optimizarRuta(
  origen: Coordenada,
  ordenes: OrdenUbicada[],
  params: ParametrosRuta = PARAMETROS_RUTA_DEFECTO,
): RutaOptimizada {
  const pendientes = [...ordenes];
  const ruta: EntidadRef[] = [];
  const eta: RutaOptimizada["eta"] = [];

  const velocidad = params.velocidad_kmh > 0 ? params.velocidad_kmh : VELOCIDAD_DEFECTO_KMH;
  let actual = origen;
  let tiempoMs = Date.parse(params.inicio);
  if (!Number.isFinite(tiempoMs)) tiempoMs = 0;

  while (pendientes.length > 0) {
    // Selección del vecino más cercano, con desempate determinista por clave.
    let mejorIdx = 0;
    let mejorDist = distanciaKm(actual, pendientes[0].coord);
    for (let i = 1; i < pendientes.length; i++) {
      const d = distanciaKm(actual, pendientes[i].coord);
      if (
        d < mejorDist ||
        (d === mejorDist && ordenKey(pendientes[i].orden_ref) < ordenKey(pendientes[mejorIdx].orden_ref))
      ) {
        mejorDist = d;
        mejorIdx = i;
      }
    }

    const [elegido] = pendientes.splice(mejorIdx, 1);
    // ETA acumulado: tiempo de viaje desde la parada previa (horas → ms).
    tiempoMs += (mejorDist / velocidad) * 3_600_000;
    ruta.push(elegido.orden_ref);
    eta.push({ orden_ref: elegido.orden_ref, eta: new Date(tiempoMs).toISOString() });
    actual = elegido.coord;
  }

  return { ruta, eta };
}

// ---------------------------------------------------------------------------
// reconciliar — Sync_Offline determinista y sin pérdida (Property 42)
// ---------------------------------------------------------------------------

/**
 * Un cambio capturado localmente sin conectividad (R8.5). `cambio_id` lo
 * identifica de forma única (idempotencia); `tipo` distingue la captura de
 * evidencia del resto de mutaciones.
 */
export interface CambioLocal {
  /** Identificador único del cambio (dedup determinista). */
  cambio_id: Id;
  /** Tipo de cambio: `"evidencia"` se registra como Evento_Operacional (R8.9). */
  tipo: string;
  /** Orden_Inspeccion afectada (R17). */
  orden_ref: EntidadRef;
  /** Instante de captura local (ISO-8601 UTC). */
  ts: IsoTimestamp;
  /** Autor del cambio (para el Evento_Operacional de evidencia, R8.9). */
  autor: string;
  /** Datos del cambio. */
  payload: Record<string, unknown>;
}

/** Versión conocida por el servidor de una entidad (para detectar conflictos). */
export interface VersionServidor {
  ts: IsoTimestamp;
}

/** Estado del servidor al recuperar conexión (versión por orden). */
export interface EstadoServidor {
  /** Versión vigente por clave de orden (`tipo:id`). */
  versiones: Record<string, VersionServidor>;
}

/** Cambio aplicado tras la reconciliación, con el criterio de resolución. */
export interface CambioReconciliado {
  cambio: CambioLocal;
  /** True si hubo conflicto con la versión del servidor. */
  conflicto: boolean;
  /** Criterio de resolución aplicado (registrado para trazabilidad, R8.6). */
  criterio: string;
  /** True si el cambio local prevalece sobre el del servidor. */
  prevalece_local: boolean;
}

/** Resultado detallado y determinista de la reconciliación offline. */
export interface ResultadoReconciliacionDetallado {
  /** Cambios procesados, en orden determinista, sin pérdida. */
  reconciliados: CambioReconciliado[];
  /** Evidencias capturadas a registrar como Evento_Operacional (R8.9). */
  evidencias: CambioLocal[];
}

/** Criterios de resolución de conflictos (trazables, R8.6). */
export const CRITERIO_SIN_CONFLICTO = "sin-conflicto";
export const CRITERIO_LWW_LOCAL = "last-write-wins:local-mas-reciente";
export const CRITERIO_LWW_SERVIDOR = "last-write-wins:servidor-mas-reciente";
export const CRITERIO_EVIDENCIA_PRESERVADA = "evidencia-preservada:sin-perdida";

/** Orden determinista total de los cambios: por (ts, cambio_id) ascendente. */
function compararCambios(a: CambioLocal, b: CambioLocal): number {
  const ta = Date.parse(a.ts);
  const tb = Date.parse(b.ts);
  if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
  if (a.ts !== b.ts) return a.ts < b.ts ? -1 : 1;
  return a.cambio_id < b.cambio_id ? -1 : a.cambio_id > b.cambio_id ? 1 : 0;
}

/**
 * Reconcilia un conjunto de cambios locales contra el estado del servidor
 * **sin pérdida** y de forma **determinista** (Property 42):
 *
 *   - Los cambios se procesan en un **orden total determinista** (por `ts`,
 *     desempatado por `cambio_id`), de modo que el mismo conjunto de
 *     cambios produce siempre el mismo resultado, sea cual sea el orden de
 *     entrada.
 *   - Un cambio entra en **conflicto** cuando el servidor tiene una versión
 *     de la misma orden con `ts` estrictamente posterior a la del cambio
 *     local. El conflicto se resuelve por *last-write-wins* y el **criterio
 *     aplicado queda registrado** en cada {@link CambioReconciliado} (R8.6).
 *   - **Ninguna evidencia se descarta**: las capturas de tipo `"evidencia"`
 *     se preservan siempre y se devuelven en `evidencias` para registrarse
 *     como Evento_Operacional, incluso si su mutación asociada perdió el
 *     conflicto (R8.9).
 *
 * No muta sus argumentos.
 */
export function reconciliar(
  cambios: CambioLocal[],
  servidor: EstadoServidor = { versiones: {} },
): ResultadoReconciliacionDetallado {
  const ordenados = [...cambios].sort(compararCambios);
  const reconciliados: CambioReconciliado[] = [];
  const evidencias: CambioLocal[] = [];

  for (const cambio of ordenados) {
    const key = ordenKey(cambio.orden_ref);
    const versionServidor = servidor.versiones[key];
    const esEvidencia = cambio.tipo === "evidencia";

    let conflicto = false;
    let prevaleceLocal = true;
    let criterio = CRITERIO_SIN_CONFLICTO;

    if (versionServidor) {
      const tServidor = Date.parse(versionServidor.ts);
      const tLocal = Date.parse(cambio.ts);
      if (Number.isFinite(tServidor) && Number.isFinite(tLocal) && tServidor > tLocal) {
        // El servidor tiene una versión más reciente → gana el servidor.
        conflicto = true;
        prevaleceLocal = false;
        criterio = CRITERIO_LWW_SERVIDOR;
      } else if (tServidor === tLocal && versionServidor.ts !== cambio.ts) {
        // Empate temporal con cadenas distintas: desempate determinista.
        conflicto = true;
        prevaleceLocal = true;
        criterio = CRITERIO_LWW_LOCAL;
      } else if (Number.isFinite(tServidor) && Number.isFinite(tLocal) && tServidor < tLocal) {
        conflicto = true;
        prevaleceLocal = true;
        criterio = CRITERIO_LWW_LOCAL;
      }
    }

    // La evidencia nunca se pierde, gane o no el conflicto su mutación (R8.9).
    if (esEvidencia) {
      evidencias.push(cambio);
      if (conflicto && !prevaleceLocal) {
        criterio = CRITERIO_EVIDENCIA_PRESERVADA;
        prevaleceLocal = true; // la evidencia se preserva aunque la mutación pierda.
      }
    }

    reconciliados.push({ cambio, conflicto, criterio, prevalece_local: prevaleceLocal });
  }

  return { reconciliados, evidencias };
}

// ---------------------------------------------------------------------------
// nfcCoincide — validación de identificador NFC/QR (R8.8)
// ---------------------------------------------------------------------------

/**
 * Determina si un identificador NFC/QR leído **corresponde** al Medidor o
 * punto de servicio de la orden activa. La comparación es exacta tras
 * normalizar mayúsculas/espacios. Si la orden no tiene identificador
 * esperado, no se puede validar y la asociación se rechaza (R8.8).
 */
export function nfcCoincide(esperado: string | undefined, leido: string): boolean {
  if (!esperado) return false;
  const norm = (s: string): string => s.trim().toLowerCase();
  return norm(esperado) === norm(leido);
}

// ---------------------------------------------------------------------------
// OfflineOrderStore — órdenes en IndexedDB para trabajo offline (R8.5)
// ---------------------------------------------------------------------------

/**
 * Almacén clave→valor de Ordenes_Inspeccion para consulta y captura de
 * evidencia sin conectividad (R8.5). Implementación por defecto respaldada
 * por IndexedDB con *fallback* en memoria (Node/Vitest/jsdom).
 */
export interface OfflineOrderStore {
  put(orden: OrdenUbicada): Promise<void>;
  get(orden_ref: EntidadRef): Promise<OrdenUbicada | null>;
  getAll(): Promise<OrdenUbicada[]>;
}

const OFFLINE_DB_NAME = "anc-field-ops";
const OFFLINE_DB_VERSION = 1;
const OFFLINE_STORE = "ordenes";

function indexedDbDisponible(): boolean {
  try {
    return typeof globalThis !== "undefined" && typeof globalThis.indexedDB !== "undefined" && globalThis.indexedDB !== null;
  } catch {
    return false;
  }
}

/** Implementación en memoria (fallback determinista para tests/Node). */
class OfflineOrderMemoryStore implements OfflineOrderStore {
  private readonly mapa = new Map<string, OrdenUbicada>();

  async put(orden: OrdenUbicada): Promise<void> {
    this.mapa.set(ordenKey(orden.orden_ref), { ...orden });
  }

  async get(orden_ref: EntidadRef): Promise<OrdenUbicada | null> {
    const o = this.mapa.get(ordenKey(orden_ref));
    return o ? { ...o } : null;
  }

  async getAll(): Promise<OrdenUbicada[]> {
    // Orden determinista por clave.
    return [...this.mapa.entries()].sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0)).map(([, v]) => ({ ...v }));
  }
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

/** Implementación respaldada por IndexedDB. */
class OfflineOrderIdbStore implements OfflineOrderStore {
  constructor(private readonly db: IDBDatabase) {}

  private store(mode: IDBTransactionMode): IDBObjectStore {
    return this.db.transaction(OFFLINE_STORE, mode).objectStore(OFFLINE_STORE);
  }

  async put(orden: OrdenUbicada): Promise<void> {
    await promisify(this.store("readwrite").put({ key: ordenKey(orden.orden_ref), orden }));
  }

  async get(orden_ref: EntidadRef): Promise<OrdenUbicada | null> {
    const rec = (await promisify(this.store("readonly").get(ordenKey(orden_ref)))) as
      | { key: string; orden: OrdenUbicada }
      | undefined;
    return rec ? rec.orden : null;
  }

  async getAll(): Promise<OrdenUbicada[]> {
    const recs = (await promisify(this.store("readonly").getAll())) as Array<{ key: string; orden: OrdenUbicada }>;
    return recs
      .slice()
      .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
      .map((r) => r.orden);
  }
}

/**
 * Abre el {@link OfflineOrderStore} respaldado por IndexedDB; si no está
 * disponible o falla, devuelve un almacén en memoria con idéntica semántica
 * (R8.5, siempre funcional y testeable sin IndexedDB real).
 */
export async function openOfflineOrderStore(): Promise<OfflineOrderStore> {
  if (!indexedDbDisponible()) return new OfflineOrderMemoryStore();
  try {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = globalThis.indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);
      req.onupgradeneeded = () => {
        const database = req.result;
        if (!database.objectStoreNames.contains(OFFLINE_STORE)) {
          database.createObjectStore(OFFLINE_STORE, { keyPath: "key" });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("No se pudo abrir IndexedDB"));
    });
    if (!db.objectStoreNames.contains(OFFLINE_STORE)) {
      db.close();
      return new OfflineOrderMemoryStore();
    }
    return new OfflineOrderIdbStore(db);
  } catch {
    return new OfflineOrderMemoryStore();
  }
}

// ---------------------------------------------------------------------------
// FieldOpsLocal — implementación demo de la interfaz FieldOps
// ---------------------------------------------------------------------------

function nowIso(): IsoTimestamp {
  return new Date().toISOString();
}

function generarId(): Id {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch {
    /* fallback abajo */
  }
  return `evi-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/** Estado conocido de una Cuadrilla en la implementación demo. */
interface EstadoCuadrillaDemo {
  coord: Coordenada;
  estado: EstadoCuadrilla;
}

/** Dependencias inyectables del {@link FieldOpsLocal}. */
export interface FieldOpsLocalOptions {
  /** Event_Store donde registrar la evidencia capturada (R8.9). */
  eventStore?: EventStore;
  /** Rol del usuario actual, para autorizar el tracking (R7.6, R8.1). */
  rolActual?: () => Rol | null;
  /** Roles autorizados a recibir tracking. Por defecto {@link ROLES_TRACKING_AUTORIZADOS}. */
  rolesAutorizados?: readonly Rol[];
  /** Autor de los Eventos_Operacionales de evidencia. */
  autorActual?: () => string;
  /** Parámetros de cálculo de ruta/ETA. */
  rutaParams?: ParametrosRuta;
  /** Almacén offline de órdenes (inyectable en tests). */
  offlineStore?: OfflineOrderStore;
  /** Estado del servidor para la reconciliación (inyectable). */
  estadoServidor?: () => EstadoServidor;
}

/**
 * Field_Ops demo client-side que implementa {@link FieldOps}. Mantiene en
 * memoria las posiciones de las Cuadrillas y delega las órdenes al
 * {@link OfflineOrderStore} para soportar el trabajo offline (R8.5). Las
 * funciones de cálculo (`route`, `reconcile`) delegan en las funciones puras
 * deterministas {@link optimizarRuta} y {@link reconciliar}.
 */
export class FieldOpsLocal implements FieldOps {
  private readonly eventStore?: EventStore;
  private readonly rolActual?: () => Rol | null;
  private readonly rolesAutorizados: readonly Rol[];
  private readonly autorActual: () => string;
  private readonly rutaParams: ParametrosRuta;
  private readonly estadoServidor: () => EstadoServidor;
  private offlineStore: OfflineOrderStore | null;
  private readonly cuadrillas = new Map<string, EstadoCuadrillaDemo>();

  constructor(opts?: FieldOpsLocalOptions) {
    this.eventStore = opts?.eventStore;
    this.rolActual = opts?.rolActual;
    this.rolesAutorizados = opts?.rolesAutorizados ?? ROLES_TRACKING_AUTORIZADOS;
    this.autorActual = opts?.autorActual ?? (() => "operador_terreno_demo");
    this.rutaParams = opts?.rutaParams ?? PARAMETROS_RUTA_DEFECTO;
    this.estadoServidor = opts?.estadoServidor ?? (() => ({ versiones: {} }));
    this.offlineStore = opts?.offlineStore ?? null;
  }

  /** Abre (perezosamente) el almacén offline de órdenes. */
  private async asegurarStore(): Promise<OfflineOrderStore> {
    if (!this.offlineStore) this.offlineStore = await openOfflineOrderStore();
    return this.offlineStore;
  }

  /** Registra/actualiza la posición y estado de una Cuadrilla (seed demo). */
  setCuadrilla(cuadrilla_id: Id, coord: Coordenada, estado: EstadoCuadrilla): void {
    this.cuadrillas.set(cuadrilla_id, { coord, estado });
  }

  /** Persiste una Orden_Inspeccion en el almacén offline (R8.5). */
  async setOrden(orden: OrdenUbicada): Promise<void> {
    const store = await this.asegurarStore();
    await store.put(orden);
  }

  /** Devuelve todas las órdenes disponibles offline (R8.5). */
  async ordenesOffline(): Promise<OrdenUbicada[]> {
    const store = await this.asegurarStore();
    return store.getAll();
  }

  /** ¿Está autorizado el rol actual a recibir tracking? (R7.6, R8.1). */
  private autorizado(): boolean {
    if (!this.rolActual) return true; // sin contexto RBAC en demo → permitido.
    const rol = this.rolActual();
    return rol !== null && this.rolesAutorizados.includes(rol);
  }

  /**
   * Posición en tiempo real de una Cuadrilla, **solo a usuarios autorizados**
   * (R7.6, R8.1). La cadencia de actualización es ≤ {@link TRACK_INTERVAL_MS}.
   */
  async track(cuadrilla_id: Id): Promise<Result<Posicion>> {
    if (!this.autorizado()) {
      return {
        ok: false,
        error: "Acceso denegado: el rol actual no está autorizado a recibir la ubicación de la Cuadrilla (R7.6, R8.1).",
      };
    }
    const c = this.cuadrillas.get(cuadrilla_id);
    if (!c) {
      return { ok: false, error: `Cuadrilla '${cuadrilla_id}' sin ubicación conocida.`, degraded: true };
    }
    return { ok: true, value: { cuadrilla_id, coord: c.coord, ts: nowIso() } };
  }

  /**
   * Optimización **determinista** de la secuencia de visitas y ETA por orden
   * (R8.2, R8.3, R8.4). Resuelve la ubicación de las órdenes desde el almacén
   * offline y parte de la posición de la Cuadrilla.
   */
  async route(cuadrilla_id: Id, ordenes: EntidadRef[]): Promise<Result<RutaOptimizada>> {
    const c = this.cuadrillas.get(cuadrilla_id);
    if (!c) {
      return { ok: false, error: `Cuadrilla '${cuadrilla_id}' sin ubicación conocida.`, degraded: true };
    }
    const store = await this.asegurarStore();
    const ubicadas: OrdenUbicada[] = [];
    const faltantes: string[] = [];
    for (const ref of ordenes) {
      const o = await store.get(ref);
      if (o) ubicadas.push(o);
      else faltantes.push(ordenKey(ref));
    }
    if (ubicadas.length === 0) {
      return {
        ok: false,
        error: `No hay ubicaciones conocidas para las órdenes solicitadas (${faltantes.join(", ")}).`,
        degraded: true,
      };
    }
    const ruta = optimizarRuta(c.coord, ubicadas, this.rutaParams);
    return { ok: true, value: ruta };
  }

  /**
   * Asocia evidencia a una Orden_Inspeccion vía NFC/QR. Si el identificador
   * leído no corresponde al Medidor/punto de servicio de la orden activa,
   * **rechaza**, **preserva la evidencia** capturada (no la descarta) y
   * devuelve error (R8.8). Si coincide, registra la evidencia como
   * Evento_Operacional con autor, timestamp y referencia a la orden (R8.9).
   */
  async associateEvidence(orden_ref: EntidadRef, nfc_qr: string): Promise<Result<AsociacionEvidencia>> {
    const store = await this.asegurarStore();
    const orden = await store.get(orden_ref);
    if (!orden) {
      return { ok: false, error: `Orden '${ordenKey(orden_ref)}' no disponible offline.`, degraded: true };
    }

    if (!nfcCoincide(orden.identificador, nfc_qr)) {
      // R8.8: rechaza, preserva la evidencia y notifica el error.
      return {
        ok: false,
        error:
          `El identificador NFC/QR '${nfc_qr}' no corresponde al Medidor o punto de servicio de la ` +
          `orden activa (esperado '${orden.identificador ?? "—"}'). La evidencia capturada se preserva (R8.8).`,
      };
    }

    const autor = this.autorActual();
    const ts = nowIso();
    const evidencia_ref: EntidadRef = { tipo: "Orden_Inspeccion", id: `evidencia-${generarId()}` };

    // R8.9: registra la evidencia como Evento_Operacional con autor/ts/ref orden.
    await this.registrarEvidencia(orden_ref, evidencia_ref, autor, ts, nfc_qr);

    return { ok: true, value: { orden_ref, evidencia_ref, autor, ts } };
  }

  /** Emite el Evento_Operacional de evidencia capturada (R8.9). No rompe ante fallo. */
  private async registrarEvidencia(
    orden_ref: EntidadRef,
    evidencia_ref: EntidadRef,
    autor: string,
    ts: IsoTimestamp,
    nfc_qr: string,
  ): Promise<void> {
    if (!this.eventStore) return;
    const evento: Omit<EventoOperacional, "event_id" | "seq" | "prev_hash" | "payload_hash"> = {
      tipo: "evidencia_capturada",
      entidad_ref: orden_ref,
      actor: autor,
      ts,
      payload: { evidencia_ref, orden_ref, nfc_qr, fuente: "field_ops" },
      correlation_id: generarId(),
    };
    try {
      await this.eventStore.append(evento);
    } catch {
      /* el registro de evidencia no debe romper el flujo de terreno */
    }
  }

  /**
   * Reconcilia los cambios capturados offline con el servidor **sin
   * pérdida**, resolviendo conflictos de forma **determinista y trazable**
   * (R8.6) y registrando toda evidencia capturada como Evento_Operacional
   * con autor/ts/ref orden (R8.9). Los cambios no tipados como
   * {@link CambioLocal} se ignoran de forma segura.
   */
  async reconcile(cambios_locales: unknown[]): Promise<Result<ResultadoReconciliacion>> {
    const cambios = cambios_locales.filter(esCambioLocal);
    const detalle = reconciliar(cambios, this.estadoServidor());

    // Registra cada evidencia capturada como Evento_Operacional (R8.9).
    for (const ev of detalle.evidencias) {
      const evidencia_ref: EntidadRef =
        (ev.payload.evidencia_ref as EntidadRef | undefined) ??
        { tipo: "Orden_Inspeccion", id: `evidencia-${ev.cambio_id}` };
      await this.registrarEvidencia(
        ev.orden_ref,
        evidencia_ref,
        ev.autor,
        ev.ts,
        String(ev.payload.nfc_qr ?? ""),
      );
    }

    const aplicados = detalle.reconciliados.filter((r) => r.prevalece_local).length;
    const conflictos = detalle.reconciliados
      .filter((r) => r.conflicto)
      .map((r) => ({ entidad_ref: r.cambio.orden_ref, criterio: r.criterio }));

    return { ok: true, value: { aplicados, conflictos } };
  }
}

/** Type guard determinista para {@link CambioLocal}. */
function esCambioLocal(x: unknown): x is CambioLocal {
  if (x === null || typeof x !== "object") return false;
  const c = x as Record<string, unknown>;
  return (
    typeof c.cambio_id === "string" &&
    typeof c.tipo === "string" &&
    typeof c.ts === "string" &&
    typeof c.autor === "string" &&
    typeof c.orden_ref === "object" &&
    c.orden_ref !== null &&
    typeof c.payload === "object" &&
    c.payload !== null
  );
}
